import styled from '@emotion/styled'
import { css } from '@emotion/react'

import media from '../../../styles/media'
import { colors, focusShadow } from '../../../styles/theme'
import { fade } from '../../../styles/keyframes'

const S = {}

S.Content = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${({ themeColor }) =>
    themeColor === 'light' ? colors.alabaster : colors.shark};
  height: 100%;
  max-width: 100%;
  opacity: 0;
  animation: ${fade} 0.15s cubic-bezier(0, 0, 0.25, 1) forwards;
  z-index: 100;
  min-width: 20rem;
  inset-block-start: 0;
  inset-inline: 0;

  ${media.sm`
    position: absolute;
    height: auto;
    min-width: 12.5rem;
    max-width: calc(100vw - 5rem);
    max-height: 28.75rem;
    z-index: 98;
  `};

  ${({ contentPosition }) =>
    contentPosition === 'bottom' &&
    css`
      ${media.sm`
        inset-block-start: 100%;
      `};
    `};

  ${({ contentPosition }) =>
    contentPosition === 'top' &&
    css`
      ${media.sm`
        inset-block: auto 100%;
      `};
    `};

  ${({ alignContent }) =>
    alignContent === 'left' &&
    css`
      ${media.sm`
        inset-inline-end: auto;
      `};
    `};

  ${({ alignContent }) =>
    alignContent === 'right' &&
    css`
      ${media.sm`
        inset-inline-start: auto;
      `};
    `};

  ${media.md`
    max-width: 23.4375rem;
  `};
`

S.Scrollable = styled.div`
  overflow-y: auto;
  flex: 1;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 0.75rem;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ themeColor }) =>
      themeColor === 'light' ? colors.silverChalice : colors.scarpaFlow};
    border: 0.1875rem solid
      ${({ themeColor }) =>
        themeColor === 'light' ? colors.alabaster : colors.shark};

    &:active {
      background-color: ${({ themeColor }) =>
        themeColor === 'light' ? colors.scarpaFlowLight : colors.silverChalice};
      border: 0.125rem solid
        ${({ themeColor }) =>
          themeColor === 'light' ? colors.alabaster : colors.shark};
    }

    @media (hover: hover) {
      &:hover {
        background-color: ${({ themeColor }) =>
          themeColor === 'light'
            ? colors.scarpaFlowLight
            : colors.silverChalice};
        border: 0.125rem solid
          ${({ themeColor }) =>
            themeColor === 'light' ? colors.alabaster : colors.shark};
      }
    }
  }

  &::-webkit-scrollbar-track {
    background: ${({ themeColor }) =>
      themeColor === 'light'
        ? 'rgba(248, 248, 248, 0.6)'
        : 'rgba(35, 37, 43, 0.6)'};
  }

  .content {
    position: relative;
    min-height: 100%;
  }

  .fade {
    position: absolute;
    width: 100%;
    min-height: 100%;
    pointer-events: none;
    inset-block-start: 0;
    inset-inline-start: 0;
    z-index: 1;

    &::before,
    &::after {
      --fade-height: 1.25rem;
      display: none;
      width: 100%;
      content: '';
      position: sticky;
      inset-inline-start: 0;
      height: var(--fade-height);
      animation: ${fade} 0.15s cubic-bezier(0, 0, 0.25, 1) forwards;
    }

    &::before {
      background-image: ${({ themeColor }) =>
        themeColor === 'light'
          ? `linear-gradient(to bottom, ${colors.alabaster}, rgba(0, 0, 0, 0))`
          : `linear-gradient(to bottom, ${colors.shark}, rgba(0, 0, 0, 0))`};
      inset-block-start: 0;
    }

    &::after {
      background-image: ${({ themeColor }) =>
        themeColor === 'light'
          ? `linear-gradient(to top, ${colors.alabaster}, rgba(0, 0, 0, 0))`
          : `linear-gradient(to top, ${colors.shark}, rgba(0, 0, 0, 0))`};
      inset-block-start: calc(100% - var(--fade-height));
    }

    ${({ isStartMarkerVisible }) =>
      isStartMarkerVisible &&
      css`
        &::before {
          display: block;
        }
      `};

    ${({ isEndMarkerVisible }) =>
      isEndMarkerVisible &&
      css`
        &::after {
          display: block;
        }
      `};
  }

  .children {
    position: relative;
    padding-block-start: 0.75rem;
    z-index: 0;

    ${media.sm`
      padding-block: 0.75rem;
    `};
  }

  .marker {
    display: block;
    height: 0;
  }
`

S.MobileHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: ${({ themeColor }) =>
    themeColor === 'light' ? colors.silver : colors.woodsmoke};
  min-height: 3.75rem;
  box-sizing: border-box;
  padding-block: 1.125rem;
  padding-inline: 1.25rem;
  box-sizing: border-box;

  ${media.sm`
    display: none;
  `};

  .heading {
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5;
    margin: 0;
    margin-inline-end: 1.125rem;
  }

  .icon-close {
    fill: currentColor;
    cursor: pointer;
    width: 1.5rem;
    height: 1.5rem;
  }

  .heading,
  .icon-close {
    color: ${({ themeColor }) =>
      themeColor === 'light' ? colors.black : colors.white};
  }
`

S.Footer = styled.div`
  display: grid;
  grid-auto-rows: 3.75rem;

  .cta {
    --button-margin: 1.25rem;
    width: calc(100% - (var(--button-margin) * 2));
    margin: 0 var(--button-margin);

    ${media.sm`
      display: none;
    `};
  }
`

S.Children = styled.div`
  .radio-option,
  .checkbox-option {
    span[type='copy'] {
      font-size: 0.875rem;
      line-height: 1.125rem;
    }
  }

  .radio-option,
  .checkbox-option {
    display: flex;
    flex: 1;
    padding: 0.6875rem 1.25rem;
    margin: 0;
    ${focusShadow};

    &:not([data-disabled='true']) {
      @media (hover: hover) {
        &:hover {
          background-color: ${({ themeColor }) =>
            themeColor === 'light' ? colors.silver : colors.woodsmoke};
        }
      }
    }

    &[data-active='true'] {
      color: ${({ themeColor }) =>
        themeColor === 'light' ? colors.black : colors.white};
    }

    &:focus, &:focus-within {
      background-color: ${({ themeColor }) =>
        themeColor === 'light' ? colors.silver : colors.woodsmoke};
    }
  }

  .text-option {
    margin: 0;
    padding: 0.75rem 1.25rem;
    color: ${({ themeColor }) =>
      themeColor === 'light' ? colors.scarpaFlow : colors.silverChalice};
    cursor: pointer;
    transition: 0.15s cubic-bezier(0.5, 0, 0.25, 1);
    transition-property: color, background-color;

    &[data-active='true'] {
      color: ${({ themeColor }) =>
        themeColor === 'light' ? colors.black : colors.alabaster};
    }

    &:not([data-disabled='true']) {
      @media (hover: hover) {
        &:hover {
          background-color: ${({ themeColor }) =>
            themeColor === 'light' ? colors.silver : colors.woodsmoke};
          color: ${({ themeColor }) =>
            themeColor === 'light' ? colors.black : colors.white};
        }
      }
    }

    &:focus {
      background-color: ${({ themeColor }) =>
        themeColor === 'light' ? colors.silver : colors.woodsmoke};
      color: ${({ themeColor }) =>
        themeColor === 'light' ? colors.black : colors.white};
    }

    &[data-disabled='true'] {
      opacity: 0.5;
      cursor: default;
    }
  }
`

S.ContentSection = styled.div`
  margin-block-end: 1.5rem;
  border: none; 
  padding: 0; 
  margin: 0;
  min-width: 0;

  &:last-of-type {
    margin-block-end: 0;
  }
`

S.ContentSectionTitle = styled.h1`
  color: ${({ themeColor }) =>
    themeColor === 'light' ? colors.black : colors.white};
  margin: 0;
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.44;
  padding: 0 1.25rem 0.25rem;
`

export default S
